import React from 'react';

import { Typography } from '@mui/material';

import { formatVppCents, valuePerPointCents, vppWindowState } from './societeRewardMargin';

interface Props {
  /** AUD LuxPlus+ price for the package. `null` renders both values as "n/a" regardless of points. */
  luxPlusPriceAud: number | null;
  /** Formula-side base-stay points: the reward's last-computed `computed_points_base`. */
  formulaPoints: number;
  /** Override/current base-stay points (override input taking precedence over the live value). */
  overridePoints: number;
  /** When `false`, renders only the override value as a single line. When `true`, renders both as "formula: … · override: …". */
  showBoth: boolean;
  /** Flash config `vppFloorCents` — with `vppCeilingCents`, drives the out-of-window warning on the override/current VPP. `null`: no window hint. */
  vppFloorCents: number | null;
  /** Flash config `vppCeilingCents` — see `vppFloorCents`. */
  vppCeilingCents: number | null;
}

/**
 * Live "VPP" (value per point) preview line shown beside the reward margin in the override
 * dialog. Recomputes from the SAME current-input precedence the margin line uses (override input
 * taking precedence over the live value), so it updates as the admin types — before anything is
 * submitted.
 *
 * The VPP mirrors the pricing service's own `valuePerPointCents` (LP+ AUD price × 100 ÷ points,
 * NO copay term), so the number here is the exact one the service gates against the flash
 * config's `vppFloorCents`/`vppCeilingCents` — and the warning hint judges it against those
 * CONFIGURED bounds (passed in by the caller from the fetched catalog config), never a hardcoded
 * window. Unknown bounds (config not loaded / absent) render no hint rather than a wrong one.
 *
 * Shows a single value (the override/current VPP) until there's something meaningful to compare
 * it against — see `ClassicRewardsMarginLine`'s doc comment for the shared display rule
 * (`showBoth`).
 */
export default function ValuePerPointLine({
  luxPlusPriceAud,
  formulaPoints,
  overridePoints,
  showBoth,
  vppFloorCents,
  vppCeilingCents,
}: Props) {
  const overrideVpp = valuePerPointCents({ luxPlusPriceAud, points: overridePoints });
  const formulaVpp = valuePerPointCents({ luxPlusPriceAud, points: formulaPoints });
  // The warning tracks the value the admin is about to store (the override/current VPP).
  const window = vppWindowState({ vppCents: overrideVpp, vppFloorCents, vppCeilingCents });
  const outOfWindow = window === 'below_floor' || window === 'above_ceiling';
  const windowHint =
    window === 'below_floor'
      ? ` (below ${vppFloorCents}¢ floor)`
      : window === 'above_ceiling'
        ? ` (above ${vppCeilingCents}¢ ceiling)`
        : '';

  if (!showBoth) {
    return (
      <Typography variant="body2" color={outOfWindow ? 'warning.main' : 'text.secondary'}>
        VPP: {formatVppCents(overrideVpp)}
        {windowHint}
      </Typography>
    );
  }

  return (
    <Typography variant="body2" color={outOfWindow ? 'warning.main' : 'text.secondary'}>
      VPP — formula: {formatVppCents(formulaVpp)} · override: {formatVppCents(overrideVpp)}
      {windowHint}
    </Typography>
  );
}
